import { useMemo } from 'react';

const STABLE_ID_FIELDS = ['_id', 'playerId', 'mongoId', 'accountId', 'id'];
const FALLBACK_ID_FIELDS = ['handle'];
export const RECENT_OFFLINE_GRACE_MS = 3 * 60 * 60 * 1000;

const ONLINE_STATUSES = new Set(['online', 'active', 'available']);
const IDLE_STATUSES = new Set(['idle', 'away', 'background']);

const normalizeId = (value) => {
  if (value === null || value === undefined) return '';
  const normalized = String(value).trim();
  return normalized || '';
};

const pushId = (ids, value) => {
  const id = normalizeId(value);
  if (id && !ids.includes(id)) ids.push(id);
};

export const getIdentityCandidates = (
  entity,
  { includeMapKey, includeFallback = true } = {},
) => {
  const ids = [];
  if (!entity || typeof entity !== 'object') {
    pushId(ids, entity);
    return ids;
  }

  STABLE_ID_FIELDS.forEach((field) => pushId(ids, entity[field]));
  pushId(ids, includeMapKey);
  if (includeFallback) {
    FALLBACK_ID_FIELDS.forEach((field) => pushId(ids, entity[field]));
  }
  return ids;
};

const normalizePresenceEntries = (presenceMap) => {
  if (!presenceMap) return [];
  if (presenceMap instanceof Map) {
    return Array.from(presenceMap.entries()).filter(([, entry]) => entry);
  }
  if (typeof presenceMap === 'object') {
    return Object.entries(presenceMap).filter(([, entry]) => entry);
  }
  return [];
};

export const buildPresenceIdentityIndex = (presenceMap) => {
  const index = new Map();
  const fallbackEntries = [];

  normalizePresenceEntries(presenceMap).forEach(([mapKey, entry]) => {
    const stableIds = getIdentityCandidates(entry, {
      includeFallback: false,
      includeMapKey: mapKey,
    });
    stableIds.forEach((id) => {
      if (!index.has(id)) index.set(id, entry);
    });

    const fallbackIds = FALLBACK_ID_FIELDS.map((field) => entry?.[field])
      .map(normalizeId)
      .filter(Boolean);
    fallbackEntries.push({ entry, ids: fallbackIds });
  });

  fallbackEntries.forEach(({ entry, ids }) => {
    ids.forEach((id) => {
      if (!index.has(id)) index.set(id, entry);
    });
  });

  return index;
};

export const buildGalaxyOccupancyIndex = (galaxyChatOnlinePlayers = []) => {
  const index = new Set();
  if (!Array.isArray(galaxyChatOnlinePlayers)) return index;

  galaxyChatOnlinePlayers.forEach((chatPlayer) => {
    getIdentityCandidates(chatPlayer).forEach((id) => index.add(id));
  });

  return index;
};

const getSubscriberKey = (subscriber) =>
  getIdentityCandidates(subscriber)[0] || '';

const normalizeTimestamp = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const resolveAvailabilityStatus = ({ presenceEntry, status, now }) => {
  if (ONLINE_STATUSES.has(status)) return 'online';
  if (IDLE_STATUSES.has(status)) return 'idle';

  if (status === 'offline') {
    const recentAt = normalizeTimestamp(
      presenceEntry?.wentOfflineAt ||
        presenceEntry?.lastSeen ||
        presenceEntry?.lastSeenAt ||
        presenceEntry?.timestamp,
    );

    if (
      recentAt &&
      Number.isFinite(now) &&
      now - recentAt >= 0 &&
      now - recentAt <= RECENT_OFFLINE_GRACE_MS
    ) {
      return 'recent';
    }
  }

  return 'none';
};

const resolveSubscriberPresence = ({
  subscriber,
  presenceIndex,
  galaxyOccupancyIndex,
  now,
}) => {
  const candidateIds = getIdentityCandidates(subscriber);
  const presenceEntry =
    candidateIds.map((id) => presenceIndex.get(id)).find(Boolean) || null;
  const status = String(
    presenceEntry?.status || presenceEntry?.presenceStatus || 'offline',
  ).toLowerCase();
  const availabilityStatus = resolveAvailabilityStatus({
    presenceEntry,
    status,
    now,
  });
  const isInGalaxy = candidateIds.some((id) => galaxyOccupancyIndex.has(id));

  return {
    status,
    availabilityStatus,
    isOnline: availabilityStatus === 'online',
    isIdle: availabilityStatus === 'idle',
    isRecentlyOffline: availabilityStatus === 'recent',
    isPresenceVisible: availabilityStatus !== 'none',
    isInGalaxy,
    presenceEntry,
  };
};

export const buildGalaxySubscriberPresence = ({
  subscribers = [],
  presenceMap,
  galaxyChatOnlinePlayers = [],
  now = Date.now(),
} = {}) => {
  const presenceIndex = buildPresenceIdentityIndex(presenceMap);
  const galaxyOccupancyIndex = buildGalaxyOccupancyIndex(
    galaxyChatOnlinePlayers,
  );

  return (Array.isArray(subscribers) ? subscribers : []).reduce(
    (acc, subscriber) => {
      const key = getSubscriberKey(subscriber);
      if (!key) return acc;
      acc[key] = resolveSubscriberPresence({
        subscriber,
        presenceIndex,
        galaxyOccupancyIndex,
        now,
      });
      return acc;
    },
    {},
  );
};

export default function useGalaxySubscriberPresence({
  subscribers = [],
  presenceMap,
  galaxyChatOnlinePlayers = [],
} = {}) {
  return useMemo(
    () =>
      buildGalaxySubscriberPresence({
        subscribers,
        presenceMap,
        galaxyChatOnlinePlayers,
      }),
    [subscribers, presenceMap, galaxyChatOnlinePlayers],
  );
}
